import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAdmin } from "@/admin/AdminContext";
import { useAuth } from "@/context/AuthContext";
import { StatusPill } from "@/admin/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { money, FREQ_LABEL } from "@/lib/brand";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Copy, Archive, Ban, Mail, Phone, Building2, Send, Lock, Unlock,
  MailPlus, PauseCircle, PlayCircle, CalendarClock, SlidersHorizontal,
  LayoutDashboard, Package, Database, FileArchive, Download, LogOut,
} from "lucide-react";
import SystemDataTab from "@/admin/client/SystemDataTab";
import FilesTab from "@/admin/client/FilesTab";
import DownloadsTab from "@/admin/client/DownloadsTab";
import OffboardingTab from "@/admin/client/OffboardingTab";
import ServiceDetailDialog from "@/admin/client/ServiceDetailDialog";

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString() : null);

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "services", label: "Services", icon: Package },
  { id: "data", label: "System Data", icon: Database },
  { id: "files", label: "Files", icon: FileArchive },
  { id: "downloads", label: "Downloads", icon: Download },
  { id: "offboarding", label: "Offboarding", icon: LogOut },
];

export default function ClientDetail() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { openNewService, version, refresh } = useAdmin();
  const { user } = useAuth();
  const isSuper = user.role === "super_admin";
  const [client, setClient] = useState(null);
  const [summary, setSummary] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [tab, setTab] = useState("overview");
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [editSvc, setEditSvc] = useState(null);

  const load = () => api.get(`/clients/${clientId}`).then((r) => setClient(r.data)).catch(() => {});
  const loadSummary = () => api.get(`/clients/${clientId}/summary`).then((r) => setSummary(r.data)).catch(() => {});
  const loadAlerts = () => api.get(`/clients/${clientId}/alerts`).then((r) => setAlerts(r.data)).catch(() => {});
  useEffect(() => { load(); loadSummary(); loadAlerts(); }, [clientId, version]);
  const resolveAlert = (id) => api.post(`/billing/alerts/${id}/resolve`).then(() => loadAlerts()).catch(() => {});

  const copyLink = async (service) => {
    try {
      if (service.annual_option && service.payment_status !== "paid") {
        const r = await api.post(`/services/${service.service_id}/send-invitation`, { origin_url: window.location.origin, send_email: false });
        await navigator.clipboard.writeText(r.data.activation_link);
        toast.success("Plan-selection link copied"); refresh(); return;
      }
      const r = await api.post("/payments/checkout", { service_id: service.service_id, origin_url: window.location.origin });
      await navigator.clipboard.writeText(r.data.checkout_url);
      toast.success("Checkout link copied"); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not generate link"); }
  };
  const cancelSub = async (s) => { try { await api.post(`/subscriptions/${s.service_id}/cancel`); toast.success("Subscription canceled"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Could not cancel"); } };
  const archiveService = async (s) => { try { await api.post(`/services/${s.service_id}/archive`); toast.success("Service archived"); load(); } catch (e) { toast.error("Could not archive"); } };
  const sendInvite = async (s) => {
    try {
      const r = await api.post(`/services/${s.service_id}/send-invitation`, { origin_url: window.location.origin });
      if (r.data.email_sent) toast.success("Payment invitation sent to client");
      else { await navigator.clipboard.writeText(r.data.activation_link); toast.success("Invitation link copied (email not configured)"); }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send invitation"); }
  };
  const toggleCore = async (s) => {
    try {
      await api.post(`/services/${s.service_id}/system-core-access`, { grant: !s.system_core_access });
      toast.success(s.system_core_access ? "System Core access revoked" : "System Core access granted"); load();
    } catch (e) { toast.error("Could not update access"); }
  };
  const setStatus = async (status) => { try { await api.post(`/clients/${clientId}/status`, { status }); toast.success(`Account ${status.replace("_", " ")}`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Could not update status"); } };
  const sendEmail = async () => {
    if (!emailForm.subject || !emailForm.message) { toast.error("Subject and message required"); return; }
    setSending(true);
    try { await api.post(`/clients/${clientId}/send-email`, emailForm); toast.success("Email sent to client"); setEmailOpen(false); setEmailForm({ subject: "", message: "" }); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send email"); }
    finally { setSending(false); }
  };

  if (!client) return null;
  const status = client.account_status || "draft";
  const suspended = ["suspended", "canceled"].includes(status);
  const services = client.services || [];

  return (
    <div className="animate-fade-up">
      <button onClick={() => navigate("/admin/clients")} className="flex items-center gap-1 text-sm text-[#4A635A] mb-4" data-testid="client-back">
        <ArrowLeft className="h-4 w-4" /> All clients
      </button>

      {/* Header */}
      <div className="bg-white border border-[#E2EBF0] rounded-xl p-5 sm:p-6 mb-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2.5 flex-wrap">
              <h1 className="font-heading font-bold text-2xl text-[#0D3B2D]" data-testid="client-detail-name">{client.name}</h1>
              <span data-testid="client-account-status"><StatusPill status={status} /></span>
            </div>
            <div className="flex flex-wrap gap-4 mt-3 text-sm text-[#4A635A]">
              <span className="flex items-center gap-1.5"><Mail className="h-4 w-4" />{client.email}</span>
              {client.phone && <span className="flex items-center gap-1.5"><Phone className="h-4 w-4" />{client.phone}</span>}
              {client.company && <span className="flex items-center gap-1.5"><Building2 className="h-4 w-4" />{client.company}</span>}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setEmailOpen(true)} data-testid="client-email-button" variant="outline" className="border-[#E2EBF0] text-[#0D3B2D] rounded-lg">
              <MailPlus className="h-4 w-4 mr-1" /> Email
            </Button>
            {isSuper && (suspended ? (
              <Button onClick={() => setStatus("active")} data-testid="client-reactivate-button" variant="outline" className="border-[#BCECD8] text-[#10B981] rounded-lg">
                <PlayCircle className="h-4 w-4 mr-1" /> Reactivate
              </Button>
            ) : (
              <Button onClick={() => setStatus("suspended")} data-testid="client-suspend-button" variant="outline" className="border-red-200 text-red-600 rounded-lg">
                <PauseCircle className="h-4 w-4 mr-1" /> Suspend
              </Button>
            ))}
            <Button onClick={() => openNewService(client.client_id)} data-testid="client-add-service-button" className="bg-[#67F2B3] hover:bg-[#4DE6A3] text-[#071A13] font-semibold rounded-lg">
              <Plus className="h-4 w-4 mr-1" /> Add Service
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto mb-5 border-b border-[#E2EBF0]" data-testid="client-tabs">
        {TABS.map((tb) => (
          <button key={tb.id} onClick={() => setTab(tb.id)} data-testid={`client-tab-${tb.id}`}
            className={`flex items-center gap-1.5 px-3.5 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === tb.id ? "border-[#10B981] text-[#0D3B2D]" : "border-transparent text-[#4A635A] hover:text-[#0D3B2D]"}`}>
            <tb.icon className="h-4 w-4" /> {tb.label}
          </button>
        ))}
      </div>

      {alerts.length > 0 && (
        <div className="mb-4 space-y-2" data-testid="client-alerts">
          {alerts.map((a) => (
            <div key={a.alert_id} data-testid={`client-alert-${a.alert_id}`}
              className="flex items-center justify-between gap-3 bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">
              <span className="min-w-0"><span className="font-semibold uppercase text-[11px] mr-2">{a.severity}</span>{a.message}</span>
              <button onClick={() => resolveAlert(a.alert_id)} data-testid={`client-resolve-alert-${a.alert_id}`} className="shrink-0 text-xs underline hover:no-underline">Resolve</button>
            </div>
          ))}
        </div>
      )}

      {/* Overview */}
      {tab === "overview" && (
        <div className="space-y-5" data-testid="client-overview">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <SummaryCard label="Active services" value={summary?.active_services ?? "—"} />
            <SummaryCard label="Monthly recurring" value={summary ? money(summary.mrr) : "—"} />
            <SummaryCard label="Total collected" value={summary ? money(summary.total_paid) : "—"} />
            <SummaryCard label="Open downloads" value={summary?.open_downloads ?? "—"} />
          </div>
          <div className="bg-white border border-[#E2EBF0] rounded-xl p-5">
            <h3 className="font-heading font-semibold text-[#0D3B2D] mb-3">Contact & account</h3>
            <div className="grid sm:grid-cols-2 gap-y-2 gap-x-6 text-sm">
              <Field label="Name" value={client.name} />
              <Field label="Email" value={client.email} />
              <Field label="Phone" value={client.phone || "—"} />
              <Field label="Company" value={client.company || "—"} />
              <Field label="Account status" value={status.replace("_", " ")} />
              <Field label="Data snapshots" value={summary?.datasets ?? "—"} />
            </div>
          </div>
        </div>
      )}

      {/* Services */}
      {tab === "services" && (
        <div>
          {services.length === 0 ? (
            <div className="bg-white border border-dashed border-[#E2EBF0] rounded-xl py-12 text-center text-sm text-[#4A635A]">No services yet for this client.</div>
          ) : (
            <div className="space-y-3" data-testid="client-services-list">
              {services.map((s) => {
                const renewal = fmtDate(s.next_renewal_date);
                const paidThrough = fmtDate(s.paid_through_date);
                const contractEnd = fmtDate(s.contract_end_date);
                const serviceEnd = fmtDate(s.service_end_date);
                return (
                  <div key={s.service_id} data-testid={`client-service-${s.service_id}`} className="bg-white border border-[#E2EBF0] rounded-xl p-4">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-[#0D3B2D]">{s.name}</span>
                          <StatusPill status={s.status} />
                          {s.payment_type === "subscription" && s.subscription_status && <StatusPill status={s.subscription_status} />}
                          {s.payment_status === "failed" && <StatusPill status="past_due" />}
                          {s.presentation_type && s.presentation_type !== "general" && (
                            <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-[#0D3B2D]/5 text-[#0D3B2D] border border-[#E2EBF0]" data-testid={`service-presentation-${s.service_id}`}>{s.presentation_type}</span>
                          )}
                        </div>
                        {s.description && <p className="text-sm text-[#4A635A] mt-1">{s.description}</p>}
                        <div className="font-mono text-sm text-[#0D3B2D] mt-2" data-testid={`service-billing-${s.service_id}`}>
                          <span className="font-semibold">{money(s.amount)}</span>{" "}
                          <span className="text-[#4A635A]">· {s.payment_type === "subscription" ? FREQ_LABEL[s.frequency] : "one-time"}</span>
                          {s.billing_plan === "yearly" && <span className="ml-2 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-[#F4FFF9] text-[#10B981] border border-[#BCECD8]" data-testid={`service-annual-discount-${s.service_id}`}>Annual discount: 20%</span>}
                          {s.annual_option && s.payment_status !== "paid" && <span className="ml-2 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-[#0D3B2D]/5 text-[#0D3B2D] border border-[#E2EBF0]" data-testid={`service-annual-offer-${s.service_id}`}>Client chooses: Monthly or Yearly (−20%)</span>}
                          {s.payment_type === "subscription" && <span className="ml-2 text-[11px] text-[#4A635A]">· Payment: {s.payment_status || "unpaid"}</span>}
                        </div>
                        {(renewal || paidThrough || contractEnd || s.cancellation_scheduled) && (
                          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-[#4A635A]" data-testid={`service-dates-${s.service_id}`}>
                            {renewal && !s.cancellation_scheduled && <span className="flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" /> Next renewal: {renewal}</span>}
                            {paidThrough && <span>Paid through: {paidThrough}</span>}
                            {contractEnd && <span>Contract ends: {contractEnd}</span>}
                            {s.cancellation_scheduled && <span className="text-amber-600 font-medium">Cancellation scheduled{serviceEnd ? ` · ends ${serviceEnd}` : ""} · renewal off</span>}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {isSuper && (
                          <Button onClick={() => setEditSvc(s)} variant="outline" size="sm" data-testid={`edit-details-${s.service_id}`} className="border-[#E2EBF0] rounded-lg text-xs">
                            <SlidersHorizontal className="h-3.5 w-3.5 mr-1" /> Details
                          </Button>
                        )}
                        {s.status !== "archived" && (
                          <Button onClick={() => copyLink(s)} variant="outline" size="sm" data-testid={`copy-link-${s.service_id}`} className="border-[#E2EBF0] rounded-lg text-xs">
                            <Copy className="h-3.5 w-3.5 mr-1" /> Copy Link
                          </Button>
                        )}
                        {isSuper && s.status !== "archived" && (
                          <Button onClick={() => sendInvite(s)} variant="outline" size="sm" data-testid={`send-invite-${s.service_id}`} className="border-[#BCECD8] text-[#10B981] rounded-lg text-xs">
                            <Send className="h-3.5 w-3.5 mr-1" /> Send Invitation
                          </Button>
                        )}
                        {isSuper && (
                          <Button onClick={() => toggleCore(s)} variant="outline" size="sm" data-testid={`core-toggle-${s.service_id}`}
                            className={`rounded-lg text-xs ${s.system_core_access ? "border-[#BCECD8] text-[#10B981]" : "border-[#E2EBF0] text-[#4A635A]"}`}>
                            {s.system_core_access ? <Unlock className="h-3.5 w-3.5 mr-1" /> : <Lock className="h-3.5 w-3.5 mr-1" />}
                            Core {s.system_core_access ? "On" : "Off"}
                          </Button>
                        )}
                        {s.payment_type === "subscription" && s.subscription_status === "active" && (
                          <Button onClick={() => cancelSub(s)} variant="outline" size="sm" data-testid={`cancel-sub-${s.service_id}`} className="border-red-200 text-red-600 rounded-lg text-xs">
                            <Ban className="h-3.5 w-3.5 mr-1" /> Cancel
                          </Button>
                        )}
                        {s.status !== "archived" && (
                          <Button onClick={() => archiveService(s)} variant="ghost" size="sm" data-testid={`archive-${s.service_id}`} className="text-[#4A635A] rounded-lg text-xs">
                            <Archive className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {tab === "data" && <SystemDataTab clientId={clientId} services={services} />}
      {tab === "files" && <FilesTab clientId={clientId} services={services} />}
      {tab === "downloads" && <DownloadsTab clientId={clientId} />}
      {tab === "offboarding" && <OffboardingTab clientId={clientId} />}

      <ServiceDetailDialog service={editSvc} open={!!editSvc} onClose={() => setEditSvc(null)} onSaved={load} />

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="sm:max-w-md" data-testid="client-email-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#0D3B2D]">Email {client.name}</DialogTitle>
            <DialogDescription className="text-[#4A635A]">Send a branded message from ECOREXS to this client.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div><Label>Subject</Label><Input value={emailForm.subject} onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })} data-testid="client-email-subject" className="bg-white border-[#E2EBF0]" /></div>
            <div><Label>Message</Label><Textarea value={emailForm.message} onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })} rows={5} data-testid="client-email-message" className="bg-white border-[#E2EBF0]" /></div>
            <Button onClick={sendEmail} disabled={sending} data-testid="client-email-send" className="w-full bg-[#0D3B2D] hover:bg-[#071A13] text-white rounded-lg">
              {sending ? "Sending…" : "Send Email"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryCard({ label, value }) {
  return (
    <div className="bg-white border border-[#E2EBF0] rounded-xl p-4" data-testid={`summary-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="font-mono font-bold text-xl text-[#0D3B2D]">{value}</div>
      <div className="text-[11px] text-[#4A635A] mt-1">{label}</div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="flex justify-between gap-4 border-b border-[#F1F5F4] py-1.5 last:border-0">
      <span className="text-[#4A635A]">{label}</span>
      <span className="text-[#0D3B2D] font-medium text-right capitalize">{value}</span>
    </div>
  );
}
